import { useMemo } from "react";
import type { DocInfo, FileItem } from "@services/api";
import { Card, Typography, Space } from "antd";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { useDownloadFile } from "../useDownloadFile";
import styles from "./docDownloadCard.module.css";

const { Title, Text } = Typography;

interface Props {
  title: string;
  docType: string;
  docs?: DocInfo[];
  className?: string;
}

function formatBytes(value?: number | null): string {
  if (value == null || isNaN(value)) return "";
  const bytes = Number(value);
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(size < 10 ? 2 : 1)} ${units[unitIndex]}`;
}

/** Кнопка-строка со своим локальным loading (через useDownloadFile). */
interface FileRowProps { file: FileItem }
function FileRow({ file }: FileRowProps) {
  const { download, loading } = useDownloadFile();

  const name = file.fileName ?? "Файл";
  const size = file.size ?? null;

  const onClick = () => {
    if (!loading) download(file);
  };

  return (
    <button
      type="button"
      className={styles.rowButton}
      onClick={onClick}
      disabled={loading}
    >
      <div className={styles.left}>
        <Text className={styles.name}>{name}</Text>
        {size != null && (
          <Text className={styles.size} type="secondary">
            {formatBytes(size)}
          </Text>
        )}
      </div>
      <div className={styles.right} aria-hidden>
        {loading ? <LoadingOutlined /> : <DownloadOutlined />}
      </div>
    </button>
  );
}

export default function DocDownloadCard({ title, docType, docs, className }: Props) {
  const docFiles: FileItem[] = useMemo(() => {
    const files = docs?.find((d) => d.docType === docType)?.files ?? [];
    // Отфильтруем пустые/пробельные fileId
    return files.filter((f) => !!f.fileId?.trim());
  }, [docs, docType]);

  if (!docFiles.length) return null;

  return (
    <Card className={className} bordered bodyStyle={{ padding: 20 }}>
      <Space align="center" size={8} style={{ marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
      </Space>

      <div className={styles.list} role="list" aria-label={`${title}: список файлов`}>
        {docFiles.map((file) => (
          <FileRow key={file.fileId} file={file} />
        ))}
      </div>
    </Card>
  );
}
